import React, { Dispatch, ReactElement, SetStateAction, useEffect, useRef, useState } from 'react';
import { FlexWrapper, MovieDetails, Text } from '@intigral/shared';
import StaticImage from './StaticImage/StaticImage';
import styles from './slide.module.sass';

export type Images = {
    smallThumbnail: string;
    thumbnail: string;
    '465': string;
    '924': string;
    '1200': string;
};

// TODO: interfaces for different promo types - EPISODE, SEASON, REWIND, etc.
export interface IPromotion {
    genre: string[];
    images: Images;
    logo: string;
    programDescription: string;
    providerLogo: string;
    buttons?: ReactElement[] | null[];
    channelNumber?: string;
    episodeNumber?: string; // EPISODE
    duration?: string; // VOD | EPISODE
    numberOfEpisodes?: string;
    programTitle?: string /* REWIND */;
    // promoType: 'BANNER' | 'CHANNEL' | 'EPISODE' | 'REWIND' | 'SEASON' | 'SERIES' | 'SUBSCRIPTION' | 'TIER' | 'VOD';
    rating?: string; // !'BANNER' | !'SUBSCRIPTION' | !'CHANNEL';
    season?: string;
    startTime?: string /* REWIND */;
    title?: string; // CHANNEL
    year?: number;
    promotionRaw: any;
    isLocked?: boolean;
    reminderText?: string;
    live?: string;
    extraContent?: React.ReactNode;
    isLockedLinear?: boolean;
}

type IPromoSlide = {
    promotion: IPromotion;
    Trailer: any;
    setPauseSlide: Dispatch<SetStateAction<boolean>>;
    active: boolean;
    goToNextSlide: () => void;
    Player: any;
    trailerDelay: number;
    priority?: boolean;
};

const PromoSlide = ({
    promotion,
    Trailer,
    setPauseSlide,
    active,
    goToNextSlide,
    Player,
    trailerDelay,
    priority,
}: IPromoSlide) => {
    const {
        buttons,
        channelNumber,
        duration,
        episodeNumber,
        genre,
        images,
        logo,
        numberOfEpisodes,
        programDescription,
        programTitle,
        providerLogo,
        rating,
        season,
        startTime,
        title,
        year,
        promotionRaw,
        isLocked,
        reminderText,
        live,
        extraContent,
    } = promotion;
    const [showTrailer, setShowTrailer] = useState(false);
    const handleOnPlayBackError = () => {
        setShowTrailer(false);
        setPauseSlide(false);
    };
    const hasTrailer =
        Boolean(promotionRaw && (promotionRaw.media || promotionRaw.liveStream)) && !promotion?.isLockedLinear;
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        Player?.destroyPlayer();
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (active && hasTrailer && promotionRaw.Id) {
            Player.setCurrentlyPlaying(promotionRaw.Id);
            if (hasTrailer && !Player.isCarouselTrailerPlaying) {
                timeoutRef.current = setTimeout(() => {
                    setShowTrailer(true);
                }, trailerDelay);
            }
        } else {
            setShowTrailer(false);
        }
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setShowTrailer(false);
        };
    }, [active]);

    return (
        <div className={styles.slide} id={promotionRaw?.guid} data-test-id={promotionRaw?.guid}>
            {showTrailer ? (
                <Trailer
                    item={promotionRaw}
                    onFinish={() => {
                        setShowTrailer(false);
                        goToNextSlide();
                    }}
                    onStart={() => {
                        setPauseSlide(true);
                    }}
                    onError={handleOnPlayBackError}
                    onDestroy={() => {
                        setShowTrailer(false);
                    }}
                    active={active}
                    setShowTrailer={setShowTrailer}
                />
            ) : (
                <StaticImage images={images} priority={priority} />
            )}

            <FlexWrapper flexDirection="column" gap={{ default: '2x', md: '5x' }} className={styles['promo-wrapper']}>
                <div className={styles['promo-name']}>
                    {live ? (
                        <Text as="h1" variant="h1" size={'40'} alignment="start" margin="0">
                            {programTitle}
                        </Text>
                    ) : logo ? (
                        <img src={logo} alt="Logo" />
                    ) : null}
                    {programTitle && (
                        <Text as="h3" variant="h3" margin="0" size={{ default: '22', md: '40' }}>
                            {programTitle}
                        </Text>
                    )}
                </div>
                <div className={`${styles['details-wrapper']} ${showTrailer ? styles['hide'] : ''}`}>
                    {programDescription && <div className={styles['promo-description']}>{programDescription}</div>}
                </div>
                {extraContent && extraContent}
                <MovieDetails
                    provider={[providerLogo]}
                    rating={rating ? rating : undefined}
                    info={[
                        ...(genre ? genre : ''),
                        season ? season : '',
                        numberOfEpisodes ? numberOfEpisodes : '',
                        episodeNumber ? episodeNumber : '',
                        !live && programTitle ? programTitle : '',
                        title ? title : '',
                        channelNumber ? channelNumber : '',
                        startTime ? startTime : '',
                        duration ? duration : '',
                        year ? `${year}` : '',
                    ]}
                    live={live}
                />

                {reminderText && (
                    <Text as="p" variant="text4" color="white" fontWeight="medium" className={styles['reminder-text']}>
                        {reminderText}
                    </Text>
                )}

                <div className={styles['promo-buttons']}>
                    {buttons?.map((b, i) => {
                        const handleTouch = (e: React.TouchEvent) => e.stopPropagation();

                        return (
                            b && (
                                <div onTouchStart={handleTouch} onTouchEnd={handleTouch} key={`promo-cta-button-${i}`}>
                                    {b}
                                </div>
                            )
                        );
                    })}
                </div>
            </FlexWrapper>
        </div>
    );
};

export default PromoSlide;
